import { FormEvent, Fragment, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCompass, IconSearch } from '../components/icons';
import { CopyButton, Empty, Skeleton, useToast } from '../components/ui';
import { api, ApiError, DetectiveAnswer, Evidence, Finding, Severity } from '../lib/api';
import { num, pct } from '../lib/format';
import { currentLocale, t } from '../lib/i18n';
import { useSession } from '../lib/session';

const EXAMPLES = [
  'Почему падают письма на Apple?',
  'Жалобы на спам в Gmail за неделю',
  'Что с письмами с неизвестным исходом?',
  'Почему письма уходят в спам у Яндекса?',
  'Hard bounce на mail.ru за 3 дня',
];

const SEVERITY: Record<Severity, { label: string; pill: string }> = {
  critical: { label: 'Критично', pill: 'bad' },
  warning: { label: 'Внимание', pill: 'warn' },
  info: { label: 'К сведению', pill: 'sent' },
};

const FOCUS_RU: Record<string, string> = {
  bounces: 'отказы',
  complaints: 'жалобы',
  unknown: 'неизвестный исход',
  failed: 'ошибки провайдера',
  suppressed: 'подавления',
  delays: 'задержки',
  auth: 'SPF / DKIM / DMARC',
};

const KIND_RU: Record<Evidence['kind'], string> = { message: 'письмо', event: 'событие', domain: 'домен', suppression: 'подавление', outbox: 'outbox' };

/** Turns "[evt_…]" citations in the answer into links to the cited event's email. */
function withCitations(text: string, hrefs: Map<string, string>): ReactNode[] {
  return text.split(/(\[(?:msg|evt|dom)_[A-Za-z0-9]+\])/g).map((part, i) => {
    const id = part.slice(1, -1);
    const href = /^\[(msg|evt|dom)_/.test(part) ? hrefs.get(id) : undefined;
    return href ? <Link key={i} to={href} className="cite">{id.slice(0, 12)}…</Link> : <Fragment key={i}>{part}</Fragment>;
  });
}

export function DetectivePage() {
  const { me } = useSession();
  const toast = useToast();
  const ai = me?.platform.ai;
  const [question, setQuestion] = useState('');
  const [since, setSince] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<DetectiveAnswer | null>(null);

  async function ask(q = question, e?: FormEvent) {
    e?.preventDefault();
    if (q.trim().length < 3) return;
    setQuestion(q);
    setBusy(true);
    try {
      setAnswer(await api.post<DetectiveAnswer>('/detective', { question: q.trim(), ...(since ? { since: new Date(since).toISOString() } : {}) }));
    } catch (err) {
      toast(err instanceof ApiError && err.code === 'NOT_FOUND' ? t('Письмо не найдено в этом пространстве') : (err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const hrefs = new Map((answer?.findings ?? []).flatMap((f) => f.evidence.map((e) => [e.id, e.href] as const)));

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Детектив логов')}</h1>
          <p>{t('Спросите словами. Ответ собирается из событий ваших писем, кодов SMTP, подавлений и проверки DNS — каждый вывод ссылается на события, письма или проверку DNS.')}</p>
        </div>
        <span className={`pill ${ai?.enabled ? 'ok' : 'suppressed'}`} title={ai?.enabled ? t('Модель только пересказывает найденные факты') : t('Ответы собираются без модели')}>
          {ai?.enabled ? t('AI-пересказ: {model}', { model: ai.model ?? '' }) : t('AI-пересказ выключен')}
        </span>
      </div>

      <form className="card card-pad stack detective-ask" onSubmit={(e) => ask(question, e)}>
        <div className="row wrap">
          <div className="detective-input">
            <IconSearch size={18} />
            <input
              className="input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('Например: почему падают письма на Apple? или msg_… — что с этим письмом?')}
              maxLength={500}
              aria-label={t('Вопрос')}
            />
          </div>
          <input className="input" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} style={{ width: 210 }} title={t('Начало периода (необязательно)')} aria-label={t('Начало периода')} />
          <button className="btn btn-primary" disabled={busy || question.trim().length < 3} data-track="detective.ask">{busy ? t('Ныряем…') : t('Спросить')}</button>
        </div>
        <div className="chips">
          {EXAMPLES.map((x) => (
            <button type="button" key={x} className="chip" onClick={() => ask(x)} disabled={busy}>{t(x)}</button>
          ))}
        </div>
      </form>

      {busy && !answer && <div className="card card-pad stack" style={{ marginTop: 16 }}><Skeleton h={22} w="60%" /><Skeleton h={80} /><Skeleton h={120} /></div>}

      {!answer && !busy && (
        <div className="card" style={{ marginTop: 16 }}>
          <Empty icon={<IconCompass size={36} />} title={t('Задайте вопрос о доставке')}>
            {t('Почтовик, период и тип проблемы распознаются в вопросе. Без периода смотрим последние 7 дней, максимум — 30. Данные других рабочих пространств не участвуют.')}
          </Empty>
        </div>
      )}

      {answer && (
        <div className="stack" style={{ marginTop: 16, opacity: busy ? 0.6 : 1 }}>
          <div className="card card-pad stack">
            <div className="row wrap small muted">
              <span>{t('Понял вопрос так:')}</span>
              <span className="chip on">{answer.parsed.provider?.label ?? answer.parsed.recipient_domain ?? t('все почтовики')}</span>
              <span className="chip">{answer.parsed.window.label}</span>
              {answer.parsed.focus.length ? answer.parsed.focus.map((f) => <span key={f} className="chip">{t(FOCUS_RU[f] ?? f)}</span>) : <span className="chip">{t('все проблемы')}</span>}
              {answer.parsed.message_id && <Link className="chip" to={`/emails/${answer.parsed.message_id}`}>{answer.parsed.message_id}</Link>}
            </div>
            <div className="detective-answer">{withCitations(answer.narration.text, hrefs)}</div>
            <div className={`callout ${answer.narration.mode === 'llm' ? 'ok' : ''}`}>
              <span>{answer.narration.notice}</span>
            </div>
            {answer.notes.map((n) => <div key={n} className="callout warn">{n}</div>)}
          </div>

          <div className="grid grid-kpi">
            {[
              [t('Писем'), num(answer.stats.messages), answer.stats.truncated ? t('показаны последние 20 000') : t('в выборке')],
              [t('Принято провайдером'), num(answer.stats.handed_off), t('доставлено по событиям: {n}', { n: num(answer.stats.delivered) })],
              [t('Hard bounce'), num(answer.stats.hard_bounces), answer.stats.handed_off ? pct(answer.stats.bounce_rate, 2) : '—'],
              [t('Жалобы'), num(answer.stats.complaints), answer.stats.handed_off ? pct(answer.stats.complaint_rate, 2) : '—'],
              [t('Исход неизвестен'), num(answer.stats.unknown), t('не повторяются сами')],
              [t('Подавлено'), num(answer.stats.suppressed), t('не отправлялись')],
            ].map(([label, value, sub]) => (
              <div key={label} className="card kpi">
                <div className="label">{label}</div>
                <div className="value">{value}</div>
                <div className="sub">{sub}</div>
              </div>
            ))}
          </div>

          {answer.findings.map((f) => <FindingCard key={f.id} f={f} />)}

          {answer.other_findings.length > 0 && (
            <>
              <h3 style={{ marginTop: 8 }}>{t('Другие проблемы за период')} <span className="small muted">{t('— не по теме вопроса')}</span></h3>
              {answer.other_findings.map((f) => <FindingCard key={f.id} f={f} />)}
            </>
          )}

          {answer.stats.by_provider.length > 0 && (
            <div className="card">
              <div className="card-head"><h3>{t('По почтовикам')}</h3><span className="small muted">{t('по домену получателя и MX')}</span></div>
              <div className="table-wrap">
              <table className="table">
                <thead><tr><th>{t('Почтовик')}</th><th>{t('Писем')}</th><th>{t('Принято')}</th><th>Hard bounce</th><th>{t('Жалобы')}</th></tr></thead>
                <tbody>
                  {answer.stats.by_provider.map((p) => (
                    <tr key={p.id} className="click" onClick={() => ask(`Проблемы с ${p.id === 'other' ? 'доставкой' : p.label} за ${answer.parsed.window.label.replace(/^последние /, '')}`)}>
                      <td><strong>{p.label}</strong></td>
                      <td>{num(p.messages)}</td>
                      <td>{num(p.handed_off)}</td>
                      <td>{num(p.hard_bounces)}</td>
                      <td>{num(p.complaints)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function FindingCard({ f }: { f: Finding }) {
  const sev = SEVERITY[f.severity];
  return (
    <div className={`card finding ${f.severity}`}>
      <div className="card-head">
        <span className={`pill ${sev.pill}`}>{t(sev.label)}</span>
        <h3>{f.title}</h3>
      </div>
      <div className="card-pad stack">
        <p style={{ color: 'var(--text-2)' }}>{f.detail}</p>
        {f.actions.length > 0 && (
          <ul className="finding-actions">
            {f.actions.map((a) => <li key={a}>{a}</li>)}
          </ul>
        )}
        {f.records?.map((r) => (
          <div key={r.fqdn + r.value} className="dns-suggest">
            <div className="row wrap small">
              <span className="pill sent">{r.type}</span>
              <code>{r.fqdn}</code>
              <span className="spacer" />
              <CopyButton small value={r.value} label={t('Копировать значение')} />
            </div>
            <pre className="code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{r.value}</pre>
            <div className="small muted">{r.purpose} {t('MailOcean не меняет DNS сам: добавьте запись у вашего DNS-провайдера и нажмите «Проверить DNS» на странице домена.')}</div>
          </div>
        ))}
        {f.evidence.length > 0 && (
          <div className="table-wrap">
            <table className="table evidence">
              <thead><tr><th>{t('Доказательство')}</th><th>{t('Что')}</th><th>{t('Когда')}</th></tr></thead>
              <tbody>
                {f.evidence.map((e) => (
                  <tr key={e.kind + e.id}>
                    <td className="evidence-ref"><Link to={e.href} className="mono small">{e.id.length > 22 ? `${e.id.slice(0, 20)}…` : e.id}</Link> <span className="muted small">{t(KIND_RU[e.kind])}</span></td>
                    <td className="small evidence-label">{e.label}</td>
                    <td className="small muted evidence-at" style={{ whiteSpace: 'nowrap' }}>{e.at ? new Date(e.at).toLocaleString(currentLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {f.evidence_total > f.evidence.length && <div className="small muted" style={{ padding: '8px 16px' }}>{t('Показано {a} из {b}.', { a: f.evidence.length, b: num(f.evidence_total) })}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
